import React, { useMemo } from 'react'
import { DesignTokens } from '../types'
import { tokensToCssVariables, getComputedTokenValues, RADIUS_VALUES } from '../utils/tokensToCss'
import { useFontLoader } from '../hooks/useFontLoader'

interface PreviewProps {
  tokens: DesignTokens
}

export function Preview({ tokens }: PreviewProps) {
  // Load fonts dynamically
  const fontsToLoad = useMemo(() => [
    tokens.typography.displayFont.family,
    tokens.typography.bodyFont.family,
    tokens.typography.monoFont.family,
  ], [tokens.typography])

  useFontLoader(fontsToLoad)

  const cssVars = useMemo(() => tokensToCssVariables(tokens), [tokens])
  const styles = useMemo(() => getComputedTokenValues(tokens), [tokens])

  const buttonRadius =
    tokens.borderRadius === 'full' ? '9999px' : RADIUS_VALUES[tokens.borderRadius]

  return (
    <div
      className="flex-1 p-6 overflow-auto"
      style={{
        ...cssVars,
        backgroundColor: 'var(--color-neutral-bg)',
        fontFamily: 'var(--font-body)',
      }}
    >
      <h2
        className="text-xs uppercase tracking-wide mb-4"
        style={{ color: 'var(--color-neutral-text-secondary)' }}
      >
        Live Preview
      </h2>

      {/* Card */}
      <div
        className="p-5 mb-6"
        style={{
          backgroundColor: 'white',
          borderRadius: 'var(--radius)',
          boxShadow: 'var(--shadow)',
          border: '1px solid var(--color-neutral-border)',
        }}
      >
        <h3
          className="mb-2"
          style={{
            color: 'var(--color-neutral-text)',
            fontFamily: 'var(--font-display)',
            fontWeight: 'var(--font-weight-display)',
            fontSize: 'var(--font-size-display)',
            lineHeight: 'var(--line-height-display)',
          }}
        >
          Welcome to Your App
        </h3>
        <p
          className="mb-4"
          style={{
            color: 'var(--color-neutral-text-secondary)',
            fontWeight: 'var(--font-weight-body)',
            fontSize: 'var(--font-size-body)',
            lineHeight: 'var(--line-height-body)',
          }}
        >
          This is how your design system will look. Customize the tokens on the left to see changes
          in real-time.
        </p>

        {/* Buttons */}
        <div className="flex flex-wrap gap-2 mb-4">
          <button
            className="px-4 py-2 text-sm font-medium text-white transition-colors"
            style={{
              backgroundColor: styles.primary,
              borderRadius: buttonRadius,
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = styles.primaryDark)}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = styles.primary)}
          >
            Primary Button
          </button>
          <button
            className="px-4 py-2 text-sm font-medium border transition-colors"
            style={{
              color: styles.primary,
              borderColor: styles.primary,
              backgroundColor: 'transparent',
              borderRadius: buttonRadius,
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = styles.primaryBg)}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
          >
            Secondary Button
          </button>
          <button
            className="px-4 py-2 text-sm font-medium transition-colors"
            style={{
              color: styles.neutralTextSecondary,
              backgroundColor: 'transparent',
              borderRadius: buttonRadius,
            }}
            onMouseEnter={(e) => (e.currentTarget.style.color = styles.neutralText)}
            onMouseLeave={(e) => (e.currentTarget.style.color = styles.neutralTextSecondary)}
          >
            Ghost Button
          </button>
        </div>

        {/* Input */}
        <div className="mb-4">
          <label
            className="block text-xs font-medium mb-1"
            style={{ color: styles.neutralTextSecondary }}
          >
            Email Address
          </label>
          <input
            type="email"
            placeholder="you@example.com"
            className="w-full px-3 py-2 text-sm outline-none transition-shadow"
            style={{
              color: styles.neutralText,
              backgroundColor: 'white',
              border: `1px solid ${styles.neutralBorder}`,
              borderRadius: styles.radius,
              fontFamily: 'var(--font-body)',
            }}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = styles.primary
              e.currentTarget.style.boxShadow = `0 0 0 3px ${styles.primaryBg}`
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = styles.neutralBorder
              e.currentTarget.style.boxShadow = 'none'
            }}
          />
        </div>

        {/* Badges */}
        <div className="flex flex-wrap gap-2">
          <span
            className="px-2.5 py-0.5 text-xs font-medium"
            style={{
              color: styles.primaryDark,
              backgroundColor: styles.primaryBg,
              borderRadius: buttonRadius,
            }}
          >
            New Feature
          </span>
          {tokens.secondaryColor && (
            <span
              className="px-2.5 py-0.5 text-xs font-medium"
              style={{
                color: styles.secondaryDark || styles.primaryDark,
                backgroundColor: styles.secondaryBg || styles.primaryBg,
                borderRadius: buttonRadius,
              }}
            >
              Secondary Badge
            </span>
          )}
          <span
            className="px-2.5 py-0.5 text-xs font-medium"
            style={{
              color: styles.neutralTextSecondary,
              backgroundColor: styles.neutralBorder,
              borderRadius: buttonRadius,
            }}
          >
            Neutral Badge
          </span>
        </div>
      </div>

      {/* Typography Sample */}
      <div
        className="p-4"
        style={{
          backgroundColor: 'white',
          borderRadius: styles.radius,
          border: `1px solid ${styles.neutralBorder}`,
        }}
      >
        <h4
          className="text-lg mb-2"
          style={{
            color: styles.neutralText,
            fontFamily: 'var(--font-display)',
            fontWeight: 'var(--font-weight-display)',
          }}
        >
          Typography
        </h4>
        <div className="text-sm space-y-2" style={{ color: styles.neutralTextSecondary }}>
          <p>
            <span className="font-medium text-gray-700">Display:</span>{' '}
            <span style={{ fontFamily: styles.fontDisplay }}>
              {tokens.typography.displayFont.family}
            </span>
          </p>
          <p>
            <span className="font-medium text-gray-700">Body:</span>{' '}
            <span style={{ fontFamily: styles.fontBody }}>
              {tokens.typography.bodyFont.family}
            </span>
          </p>
          <p>
            <span className="font-medium text-gray-700">Mono:</span>{' '}
            <code
              className="px-1.5 py-0.5 rounded text-xs"
              style={{
                fontFamily: 'var(--font-mono)',
                backgroundColor: styles.neutralBorder,
              }}
            >
              {tokens.typography.monoFont.family}
            </code>
          </p>
        </div>
        <p
          className="mt-3"
          style={{
            color: styles.neutralText,
            fontFamily: 'var(--font-body)',
            fontSize: 'var(--font-size-body)',
            lineHeight: 'var(--line-height-body)',
          }}
        >
          The quick brown fox jumps over the lazy dog.
        </p>
        <p
          className="mt-2"
          style={{
            color: styles.neutralText,
            fontFamily: 'var(--font-display)',
            fontWeight: 'var(--font-weight-display)',
            fontSize: 'var(--font-size-display)',
            lineHeight: 'var(--line-height-display)',
          }}
        >
          The quick brown fox jumps over the lazy dog.
        </p>
      </div>
    </div>
  )
}
